import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  createExternalApiKey,
  externalApiError,
  loadExternalApiKey,
  regenerateExternalApiKey,
  revokeExternalApiKey,
  sendExternalApiEmailOtp,
  updateExternalApiIp,
  verifyExternalApiEmailOtp,
  verifyExternalApiGoogleOtp,
} from "../../actions/external-api-action";
import { canAccessDeveloperApi } from "../../utils/DeveloperApiAccess";
import ExternalApiVerificationModal from "./ExternalApiVerificationModal";
import { ExternalApiRevokeModal, ExternalApiSecretModal } from "./ExternalApiKeyModals";
import "./ExternalApiPanel.scss";

const FLOW = {
  create: "create",
  regenerate: "regenerate",
  ip: "ip",
};

const emptyVerification = { email_otp: "", google_otp: "" };
const emptyApiState = {
  loading: true,
  eligible: false,
  google_2fa_enabled: false,
  key: null,
};

const flowTitleKeys = {
  [FLOW.create]: "developer-api-create-title",
  [FLOW.regenerate]: "developer-api-regenerate-title",
  [FLOW.ip]: "developer-api-change-ip-title",
};

const successMessageKeys = {
  [FLOW.create]: "developer-api-create-success",
  [FLOW.regenerate]: "developer-api-regenerate-success",
  [FLOW.ip]: "developer-api-change-ip-success",
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const getDaysLeft = (expiresAt) =>
  expiresAt ? Math.ceil((new Date(expiresAt) - new Date()) / 86400000) : null;

const isActiveKey = (key) =>
  key?.status === "active" && key?.expires_at && new Date(key.expires_at) > new Date();

const isEnabled = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const useExternalApiVerification = () => {
  const [codes, setCodes] = useState(emptyVerification);
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState({ email: false, google: false });
  const [googleExpiresAt, setGoogleExpiresAt] = useState(null);
  const [googleSecondsLeft, setGoogleSecondsLeft] = useState(0);

  const reset = useCallback(() => {
    setCodes(emptyVerification);
    setOtpSent(false);
    setVerified({ email: false, google: false });
    setGoogleExpiresAt(null);
    setGoogleSecondsLeft(0);
  }, []);

  const setCode = (name, value) => {
    setCodes((current) => ({ ...current, [name]: value }));
  };

  const resetEmail = useCallback(() => {
    setVerified((current) => ({ ...current, email: false }));
    setCodes((current) => ({ ...current, email_otp: "" }));
    setOtpSent(false);
  }, []);

  const resetGoogle = useCallback(() => {
    setVerified((current) => ({ ...current, google: false }));
    setCodes((current) => ({ ...current, google_otp: "" }));
    setGoogleExpiresAt(null);
    setGoogleSecondsLeft(0);
  }, []);

  const markEmailSent = () => {
    setOtpSent(true);
    setVerified((current) => ({ ...current, email: false }));
  };

  const markEmailVerified = () => {
    setVerified((current) => ({ ...current, email: true }));
  };

  const markGoogleVerified = (expiresIn = 300) => {
    setVerified((current) => ({ ...current, google: true }));
    setGoogleExpiresAt(Date.now() + Number(expiresIn || 300) * 1000);
  };

  useEffect(() => {
    if (!verified.google || !googleExpiresAt) return undefined;

    const updateRemaining = () => {
      const remaining = Math.max(
        0,
        Math.ceil((googleExpiresAt - Date.now()) / 1000)
      );
      setGoogleSecondsLeft(remaining);

      if (!remaining) {
        resetGoogle();
      }
    };

    updateRemaining();
    const timer = setInterval(updateRemaining, 1000);
    return () => clearInterval(timer);
  }, [googleExpiresAt, resetGoogle, verified.google]);

  return {
    codes,
    googleSecondsLeft,
    otpSent,
    verified,
    markEmailSent,
    markEmailVerified,
    markGoogleVerified,
    reset,
    resetEmail,
    resetGoogle,
    setCode,
  };
};

const ExternalApiAccessNotice = ({ t, type }) => {
  if (type === "security") {
    return (
      <Alert variant="warning">
        {t("developer-api-security-warning")}{" "}
        <Link to="/setting?loc=6">{t("developer-api-enable-security-link")}</Link>.
      </Alert>
    );
  }

  if (type === "server") {
    return <Alert variant="warning">{t("developer-api-plan-warning")}</Alert>;
  }

  return <Alert variant="warning">{t("developer-api-plan-warning")}</Alert>;
};

const ExternalApiEmptyState = ({ onCreate, t }) => (
  <div className="external-api-card external-api-card--empty">
    <div>
      <h6>{t("developer-api-empty-title")}</h6>
      <p>{t("developer-api-empty-description")}</p>
    </div>
    <Button className="blue-button" onClick={onCreate}>
      {t("developer-api-create-button")}
    </Button>
  </div>
);

const ExternalApiKeyCard = ({ apiKey, daysLeft, onChangeIp, onRegenerate, onRevoke, t }) => (
  <div className="external-api-card external-api-card--key">
    {daysLeft !== null && daysLeft <= 30 && (
      <Alert variant="warning">
        {t("developer-api-key-expiring", { days: Math.max(daysLeft, 0) })}
      </Alert>
    )}
    <div className="external-api-card__toolbar">
      <span className="external-api-eyebrow">{t("developer-api-active-key")}</span>
      <span className="external-api-status">{t("developer-api-active-status")}</span>
    </div>

    <div className="external-api-key-preview">
      <span>{t("developer-api-key-label")}</span>
      <strong>********{apiKey.display_suffix}</strong>
    </div>

    <div className="external-api-meta-grid">
      <div className="external-api-meta-item">
        <span>{t("developer-api-allowed-ip")}</span>
        <strong>{apiKey.allowed_ip || "-"}</strong>
      </div>
      <div className="external-api-meta-item">
        <span>{t("developer-api-created")}</span>
        <strong>{formatDateTime(apiKey.created_at)}</strong>
      </div>
      <div className="external-api-meta-item">
        <span>{t("developer-api-expires")}</span>
        <strong>{formatDateTime(apiKey.expires_at)}</strong>
      </div>
      <div className="external-api-meta-item">
        <span>{t("developer-api-last-used")}</span>
        <strong>
          {apiKey.last_used_at
            ? formatDateTime(apiKey.last_used_at)
            : t("developer-api-never")}
        </strong>
      </div>
    </div>

    <div className="external-api-permissions">
      <span>{t("developer-api-permissions")}</span>
      <div>
        {(apiKey.scopes || []).length
          ? apiKey.scopes.map((scope) => (
              <strong key={scope}>{scope}</strong>
            ))
          : <strong>-</strong>}
      </div>
    </div>

    <div className="external-api-actions">
      <Button variant="light" className="external-api-action-button" onClick={onChangeIp}>
        <i className="fas fa-network-wired" />
        {t("developer-api-change-ip-button")}
      </Button>
      <Button variant="light" className="external-api-action-button" onClick={onRegenerate}>
        <i className="fas fa-sync-alt" />
        {t("developer-api-regenerate-button")}
      </Button>
      <Button
        variant="light"
        className="external-api-action-button external-api-action-button--danger"
        onClick={onRevoke}
      >
        <i className="fas fa-trash-alt" />
        {t("developer-api-revoke-button")}
      </Button>
    </div>
    <small className="external-api-footnote">
      <i className="fas fa-shield-alt" />
      {t("developer-api-footnote")}
    </small>
  </div>
);

const ExternalApiPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const hasPlanAccess = canAccessDeveloperApi(user);
  const [apiState, setApiState] = useState(emptyApiState);
  const [loadError, setLoadError] = useState(null);
  const [flow, setFlow] = useState(null);
  const [allowedIp, setAllowedIp] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealedKey, setRevealedKey] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const verification = useExternalApiVerification();

  const refresh = useCallback(() => {
    if (!hasPlanAccess) {
      setApiState({ ...emptyApiState, loading: false, eligible: false });
      setLoadError(null);
      return Promise.resolve();
    }

    setApiState((current) => ({ ...current, loading: true }));
    setLoadError(null);

    return dispatch(loadExternalApiKey())
      .then((data) => {
        setApiState({ ...emptyApiState, ...data, loading: false });
        setLoadError(null);
      })
      .catch((error) => {
        const status = Number(error?.response?.status || 0);
        const auth = status === 401 || status === 403;

        setApiState((current) => ({ ...current, loading: false, key: null }));
        setLoadError({
          auth,
          message: auth
            ? t("developer-api-auth-required")
            : externalApiError(error),
        });
      });
  }, [dispatch, hasPlanAccess, t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openFlow = (name) => {
    setFlow(name);
    setAllowedIp(name === FLOW.create ? "" : apiState.key?.allowed_ip || "");
    verification.reset();
  };

  const closeFlow = () => {
    if (busy) return;

    setFlow(null);
    verification.reset();
  };

  const forceCloseFlow = () => {
    setFlow(null);
    verification.reset();
  };

  const sendOtp = () => {
    setBusy(true);
    dispatch(sendExternalApiEmailOtp())
      .then(() => {
        verification.markEmailSent();
        toast.success(t("developer-api-email-code-sent"));
      })
      .catch((error) => toast.error(externalApiError(error)))
      .finally(() => setBusy(false));
  };

  const verifyEmailOtp = () => {
    if (!verification.codes.email_otp) {
      toast.error(t("developer-api-enter-email-code"));
      return;
    }

    setBusy(true);
    dispatch(verifyExternalApiEmailOtp(verification.codes.email_otp))
      .then(() => {
        verification.markEmailVerified();
        toast.success(t("developer-api-email-code-verified"));
      })
      .catch((error) => toast.error(externalApiError(error)))
      .finally(() => setBusy(false));
  };

  const verifyGoogleOtp = () => {
    if (!verification.codes.google_otp) {
      toast.error(t("developer-api-enter-google-code"));
      return;
    }

    setBusy(true);
    dispatch(verifyExternalApiGoogleOtp(verification.codes.google_otp))
      .then((data) => {
        verification.markGoogleVerified(data?.expires_in);
        toast.success(t("developer-api-google-verified"));
      })
      .catch((error) => toast.error(externalApiError(error)))
      .finally(() => setBusy(false));
  };

  const resetVerificationForError = (error) => {
    const code = error?.response?.data?.code;

    if (code === "google_verification_required") {
      verification.resetGoogle();
    } else if (code === "email_verification_required") {
      verification.resetEmail();
    } else {
      verification.reset();
    }
  };

  const submit = () => {
    if (!allowedIp.trim() || !verification.verified.email || !verification.verified.google) {
      toast.error(t("developer-api-security-checks-required"));
      return;
    }

    const action =
      flow === FLOW.create
        ? createExternalApiKey
        : flow === FLOW.regenerate
        ? regenerateExternalApiKey
        : updateExternalApiIp;
    const currentFlow = flow;

    setBusy(true);
    dispatch(action({ allowed_ip: allowedIp.trim() }))
      .then((data) => {
        if (data?.api_key) {
          setRevealedKey(data.api_key);
        }

        forceCloseFlow();
        return refresh();
      })
      .then(() => toast.success(t(successMessageKeys[currentFlow])))
      .catch((error) => {
        resetVerificationForError(error);
        toast.error(externalApiError(error));
      })
      .finally(() => setBusy(false));
  };

  const revoke = () => {
    setBusy(true);
    dispatch(revokeExternalApiKey())
      .then(() => {
        setConfirmRevoke(false);
        return refresh();
      })
      .then(() => toast.success(t("developer-api-revoke-success")))
      .catch((error) => toast.error(externalApiError(error)))
      .finally(() => setBusy(false));
  };

  const key = apiState.key;
  const active = isActiveKey(key);
  const daysLeft = getDaysLeft(key?.expires_at);
  const backendEligible = isEnabled(apiState.eligible);
  const canUseDeveloperApi = hasPlanAccess && backendEligible;

  return (
    <div className="setting-general-content external-api-panel">
      <div className="external-api-panel__header">
        <div>
          <h5>{t("developer-api-title")}</h5>
          <p>{t("developer-api-description")}</p>
        </div>
      </div>

      {apiState.loading && <p>{t("developer-api-loading")}</p>}
      {!apiState.loading && !hasPlanAccess && <ExternalApiAccessNotice t={t} />}
      {!apiState.loading && hasPlanAccess && loadError && (
        <Alert variant={loadError.auth ? "warning" : "danger"}>
          {loadError.message}
        </Alert>
      )}
      {!apiState.loading && hasPlanAccess && !loadError && !backendEligible && (
        <ExternalApiAccessNotice t={t} type="server" />
      )}
      {!apiState.loading &&
        canUseDeveloperApi &&
        !loadError &&
        !apiState.google_2fa_enabled && <ExternalApiAccessNotice t={t} type="security" />}
      {!apiState.loading &&
        canUseDeveloperApi &&
        !loadError &&
        apiState.google_2fa_enabled &&
        !active && <ExternalApiEmptyState t={t} onCreate={() => openFlow(FLOW.create)} />}
      {!apiState.loading &&
        canUseDeveloperApi &&
        !loadError &&
        active && (
          <ExternalApiKeyCard
            apiKey={key}
            daysLeft={daysLeft}
            onChangeIp={() => openFlow(FLOW.ip)}
            onRegenerate={() => openFlow(FLOW.regenerate)}
            onRevoke={() => setConfirmRevoke(true)}
            t={t}
          />
        )}

      <ExternalApiVerificationModal
        allowedIp={allowedIp}
        busy={busy}
        show={Boolean(flow)}
        title={flow ? t(flowTitleKeys[flow]) : ""}
        onCancel={closeFlow}
        onChangeAllowedIp={setAllowedIp}
        onSendEmailOtp={sendOtp}
        onSubmit={submit}
        onVerifyEmailOtp={verifyEmailOtp}
        onVerifyGoogleOtp={verifyGoogleOtp}
        t={t}
        verification={verification}
      />
      <ExternalApiSecretModal
        apiKey={revealedKey}
        onClose={() => setRevealedKey("")}
        t={t}
      />
      <ExternalApiRevokeModal
        busy={busy}
        show={confirmRevoke}
        onCancel={() => setConfirmRevoke(false)}
        onConfirm={revoke}
        t={t}
      />
    </div>
  );
};

export default ExternalApiPanel;
